import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Heart, Shield } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_gen-z-jewels-1/artifacts/8dp6l3ul_nova.png";

const About = () => {
  return (
    <div className="min-h-screen bg-[#FDFBF7] pt-32 pb-24 px-6" data-testid="about-page">
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Header */}
          <div className="text-center mb-16">
            <div className="flex justify-center mb-6">
              <div className="h-16 overflow-hidden">
                <img src={LOGO_URL} alt="NOVA" className="h-44 w-auto max-w-none" />
              </div>
            </div>
            <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#7A8A73] mb-4">Our Story</p>
            <h1 className="text-4xl lg:text-5xl font-serif tracking-tight text-[#2D3B26] leading-[1.1] mb-6">
              Jewelry for the<br /><span className="italic text-[#C27054]">Modern Soul</span>
            </h1>
            <p className="text-lg text-[#475740] leading-relaxed max-w-2xl mx-auto">
              NOVA was born from a simple idea: jewelry should feel as modern as the people who wear it. No more boring, traditional designs. Just bold, minimal pieces that let you express who you truly are.
            </p>
          </div>

          {/* Mission */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-20">
            <div className="bg-[#E4E8DF] rounded-3xl overflow-hidden aspect-[4/5]">
              <img
                src="https://images.unsplash.com/photo-1598218940656-7126545fd283?w=600&q=80"
                alt="About NOVA"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex flex-col justify-center">
              <h2 className="text-3xl font-serif text-[#2D3B26] mb-6">Our Mission</h2>
              <p className="text-base text-[#475740] leading-relaxed mb-6">
                We believe every generation deserves jewelry that speaks their language. NOVA is built for Gen Z and millennials who refuse to settle for the ordinary.
              </p>
              <p className="text-base text-[#475740] leading-relaxed mb-6">
                Every piece in our collection is designed with intention — modern aesthetics, premium materials, and a commitment to quality that lasts a lifetime.
              </p>
              <p className="text-base text-[#475740] leading-relaxed">
                From 14K and 18K gold to 925 sterling silver, every item is BIS Hallmark Certified, ensuring you get nothing but the best.
              </p>
            </div>
          </div>

          {/* Values */}
          <div className="mb-20">
            <h2 className="text-3xl font-serif text-[#2D3B26] text-center mb-12">What We Stand For</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-8 text-center"
              >
                <div className="w-14 h-14 bg-[#E4E8DF] rounded-full flex items-center justify-center mx-auto mb-5">
                  <Sparkles size={22} className="text-[#C27054]" />
                </div>
                <h3 className="text-lg font-serif text-[#2D3B26] mb-3">Modern Design</h3>
                <p className="text-sm text-[#475740] leading-relaxed">
                  Clean lines, bold silhouettes, and contemporary aesthetics that match your vibe.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.15 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-8 text-center"
              >
                <div className="w-14 h-14 bg-[#E4E8DF] rounded-full flex items-center justify-center mx-auto mb-5">
                  <Shield size={22} className="text-[#C27054]" />
                </div>
                <h3 className="text-lg font-serif text-[#2D3B26] mb-3">Certified Quality</h3>
                <p className="text-sm text-[#475740] leading-relaxed">
                  Every piece is BIS Hallmark Certified with a lifetime warranty you can trust.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-8 text-center"
              >
                <div className="w-14 h-14 bg-[#E4E8DF] rounded-full flex items-center justify-center mx-auto mb-5">
                  <Heart size={22} className="text-[#C27054]" />
                </div>
                <h3 className="text-lg font-serif text-[#2D3B26] mb-3">Made with Love</h3>
                <p className="text-sm text-[#475740] leading-relaxed">
                  Crafted by skilled artisans who pour their heart into every single piece.
                </p>
              </motion.div>
            </div>
          </div>

          {/* CTA */}
          <div className="bg-[#E4E8DF] rounded-3xl p-12 text-center">
            <h2 className="text-3xl font-serif text-[#2D3B26] mb-4">Ready to Shine?</h2>
            <p className="text-base text-[#475740] mb-8 max-w-lg mx-auto">
              Explore our collection and find the piece that defines your style.
            </p>
            <a
              href="/products"
              data-testid="about-shop-button"
              className="inline-block bg-[#C27054] text-white rounded-full px-10 py-4 hover:bg-[#A85B42] transition-all duration-300 shadow-[0_4px_14px_0_rgba(194,112,84,0.39)] hover:-translate-y-0.5 text-sm uppercase tracking-widest font-semibold"
            >
              Shop Collection
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default About;
